import { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  History, Loader2, RefreshCw, Search, ExternalLink, Download, Printer,
  RefreshCcw, XCircle, AlertTriangle, PlayCircle, ArrowDownCircle, CreditCard,
} from 'lucide-react';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};
const usd = (n) => (n === null || n === undefined ? '—'
  : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// kind → icon + accent
const KIND_META = {
  subscribed: { icon: CreditCard, color: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
  upgrade: { icon: CreditCard, color: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
  renewal: { icon: RefreshCcw, color: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
  payment_failed: { icon: AlertTriangle, color: 'text-rose-600', bg: 'bg-rose-50', ring: 'ring-rose-200' },
  canceled_scheduled: { icon: XCircle, color: 'text-amber-600', bg: 'bg-amber-50', ring: 'ring-amber-200' },
  canceled_immediate: { icon: XCircle, color: 'text-rose-600', bg: 'bg-rose-50', ring: 'ring-rose-200' },
  resumed: { icon: PlayCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
  downgraded: { icon: ArrowDownCircle, color: 'text-slate-600', bg: 'bg-slate-100', ring: 'ring-slate-200' },
};

export default function BillingHistoryTab() {
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loading, setLoading] = useState(false);

  const loadOrgs = async () => {
    setLoadingOrgs(true);
    try {
      const { data } = await api.get('/super-admin/subscription/billing-history/orgs');
      setOrgs(data?.orgs || []);
    } catch { toast.error('Could not load organizations'); }
    finally { setLoadingOrgs(false); }
  };

  const loadHistory = async (accountId) => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/super-admin/subscription/billing-history', {
        params: { account_id: accountId },
      });
      setData(data);
    } catch { toast.error('Could not load billing history'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadOrgs(); }, []);

  const exportCsv = async () => {
    if (!selected) return;
    try {
      const res = await api.get('/super-admin/subscription/billing-history/export.csv', {
        params: { account_id: selected.account_id }, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing_history_${(selected.name || 'org').replace(/[^a-z0-9]/gi, '_')}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not export CSV'); }
  };

  const printPdf = () => {
    if (!selected || !data) return;
    const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (m) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]));
    const rows = (data.events || []).map((e) => `
      <tr>
        <td>${esc(fmt(e.created_at))}</td>
        <td>${esc(e.label || e.kind)}</td>
        <td style="text-align:right">${e.amount_usd != null ? esc(usd(e.amount_usd)) : ''}</td>
        <td>${esc(e.plan_label || e.plan_id || '')}</td>
        <td>${esc(e.status || '')}</td>
        <td>${esc(e.detail || '')}</td>
      </tr>`).join('');
    const html = `<!doctype html><html><head><title>Billing History — ${esc(selected.name)}</title>
      <style>body{font-family:Arial,sans-serif;color:#0f172a;padding:24px}
      h1{font-size:20px}h2{font-size:13px;color:#64748b;font-weight:400}
      table{width:100%;border-collapse:collapse;margin-top:16px;font-size:12px}
      th,td{border-bottom:1px solid #e2e8f0;padding:8px;text-align:left}
      th{background:#f1f5f9;text-transform:uppercase;font-size:10px;letter-spacing:1px}
      .snap{margin-top:8px;font-size:13px}</style></head><body>
      <h1>Billing History — ${esc(selected.name)}</h1>
      <h2>${esc(selected.account_id)}</h2>
      <div class="snap">Plan: <b>${esc(sub?.plan_id || 'Free')}</b> · Status: <b>${esc(sub?.status || '—')}</b>
        · Lifetime paid: <b>${esc(usd(data?.lifetime?.paid_total_usd))}</b> (${esc(data?.lifetime?.payments_count || 0)} payments)</div>
      <table><thead><tr><th>Date</th><th>Event</th><th style="text-align:right">Amount</th><th>Plan</th><th>Status</th><th>Detail</th></tr></thead>
      <tbody>${rows || '<tr><td colspan=6>No events</td></tr>'}</tbody></table>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Allow pop-ups to print/save as PDF'); return; }
    w.document.write(html); w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return orgs;
    return orgs.filter((o) => (o.name || '').toLowerCase().includes(term)
      || (o.account_id || '').toLowerCase().includes(term));
  }, [orgs, q]);

  const sub = data?.subscription || {};

  return (
    <div className="max-w-6xl" data-testid="billing-history-tab">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-xl font-black tracking-tighter flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <History className="h-5 w-5 text-primary" /> Customer Billing History
          </h2>
          <p className="text-sm text-slate-600 mt-2">A per-org timeline of subscriptions, renewals, failed payments and cancellations — so support can see a customer's billing at a glance.</p>
        </div>
        <Button variant="outline" size="sm" className="rounded-none" onClick={loadOrgs} data-testid="billing-history-refresh-orgs">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Org picker */}
        <div className="md:col-span-1">
          <div className="relative mb-3">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search organizations…"
              data-testid="billing-history-search"
              className="w-full rounded-none border border-slate-200 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-primary"
            />
          </div>
          <div className="border border-slate-200 max-h-[520px] overflow-auto" data-testid="billing-history-org-list-container">
            {loadingOrgs ? (
              <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="h-4 w-4 animate-spin mr-2" />Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">No paying organizations yet.</div>
            ) : filtered.map((o) => (
              <button
                key={o.account_id}
                onClick={() => { setSelected(o); loadHistory(o.account_id); }}
                data-testid={`billing-history-org-${o.account_id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors ${
                  selected?.account_id === o.account_id ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                }`}
              >
                <div className="font-medium text-sm">{o.name}</div>
                <div className="text-[11px] text-slate-400 font-mono">{o.account_id}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="md:col-span-2">
          {!selected ? (
            <div className="border border-dashed border-slate-200 px-6 py-20 text-center text-sm text-slate-400" data-testid="billing-history-empty">
              Select an organization to view its billing history.
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading history…</div>
          ) : (
            <div data-testid="billing-history-detail">
              {/* Subscription snapshot */}
              <div className="border border-slate-200 p-5 mb-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <Snap label="Current plan" value={sub?.plan_id ? (sub.plan_id.charAt(0).toUpperCase() + sub.plan_id.slice(1)) : 'Free'} />
                <Snap label="Status" value={sub?.status || '—'} accent={sub?.status === 'past_due' ? 'text-rose-600' : sub?.status === 'active' ? 'text-emerald-600' : ''} />
                <Snap label={sub?.cancel_at_period_end ? 'Cancels on' : 'Renews on'} value={sub?.current_period_end ? fmt(sub.current_period_end).split(',')[0] : '—'} accent={sub?.cancel_at_period_end ? 'text-amber-600' : ''} />
                <Snap label="Lifetime paid" value={usd(data?.lifetime?.paid_total_usd)} sub={`${data?.lifetime?.payments_count || 0} payment(s)`} />
              </div>

              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Timeline</div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={exportCsv} data-testid="billing-history-export-csv">
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={printPdf} data-testid="billing-history-print-pdf">
                    <Printer className="h-3.5 w-3.5 mr-1.5" /> Print / PDF
                  </Button>
                </div>
              </div>
              <div className="border border-slate-200" data-testid="billing-history-timeline">
                {(data?.events || []).length === 0 ? (
                  <div className="px-6 py-10 text-center text-sm text-slate-400">No billing events recorded yet.</div>
                ) : (
                  <div>
                    {data.events.map((e) => {
                      const meta = KIND_META[e.kind] || { icon: History, color: 'text-slate-600', bg: 'bg-slate-100', ring: 'ring-slate-200' };
                      const Icon = meta.icon;
                      return (
                        <div key={e.id} className="flex items-start gap-3 px-4 py-3 border-b border-slate-100 last:border-0" data-testid={`billing-event-${e.kind}`}>
                          <div className={`mt-0.5 h-8 w-8 rounded-full ${meta.bg} ring-1 ${meta.ring} flex items-center justify-center shrink-0`}>
                            <Icon className={`h-4 w-4 ${meta.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-semibold text-sm">{e.label}</span>
                              {e.amount_usd !== null && e.amount_usd !== undefined && (
                                <span className={`text-sm font-bold ${meta.color}`}>{usd(e.amount_usd)}</span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500">{e.detail}</div>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-[11px] text-slate-400">{fmt(e.created_at)}</span>
                              {e.hosted_invoice_url && (
                                <a href={e.hosted_invoice_url} target="_blank" rel="noreferrer"
                                  className="text-[11px] text-primary hover:underline inline-flex items-center gap-1">
                                  Invoice <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                              {e.actor && e.actor !== 'stripe' && (
                                <span className="text-[11px] text-slate-400">by {e.actor}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Snap({ label, value, sub, accent = '' }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`text-lg font-black mt-0.5 capitalize ${accent}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
}
